<template>
  <modal
    :show="isVisible"
    :title="t('Marcar taxa de implantação como paga')"
    elem-id="modal_setup_fee_mark_paid"
    size="compactDesktop"
    :close-label="t('Cancelar')"
    :save-label="t('Confirmar pagamento')"
    :save-fn="handleSave"
    :save-disabled-fn="() => isSaving || !franquiaId"
    save-test-id="setup-fee-mark-paid-submit"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <div class="mark-paid-fields">
        <franchise-select
          v-if="!hasFixedFranchise"
          v-model="franquiaId"
          elem-id="setup-fee-mark-paid-franchise"
          :label="t('Franquia')"
          data-testid="setup-fee-mark-paid-franchise-select"
        />
        <div v-else class="franchise-info">
          <label class="info-label">{{ t('Franquia') }}:</label>
          <span class="info-value" data-testid="setup-fee-mark-paid-franchise-name">{{ franchiseName || franquiaId }}</span>
        </div>

        <input-text
          v-model="reason"
          elem-id="setup-fee-mark-paid-reason"
          :label="t('Motivo') + ' (' + t('opcional') + ')'"
          data-testid="setup-fee-mark-paid-reason-input"
        />

        <div v-if="errorMessage" class="error-message" data-testid="setup-fee-mark-paid-error">
          {{ errorMessage }}
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { useSetupFee } from '../../../composables/useSetupFee'
import { log } from '../../../utils/DebugLogger'
import Modal from '../../../ui/Modal.vue'
import FranchiseSelect from '../../../inputs/FranchiseSelect.vue'
import InputText from '../../../inputs/InputText.vue'

const { t } = useI18n()
const { markPaid } = useSetupFee()

const isVisible = ref(false)
const isSaving = ref(false)
const hasFixedFranchise = ref(false)
const franquiaId = ref<number | string | null>(null)
const franchiseName = ref<string>('')
const reason = ref<string>('')
const errorMessage = ref<string>('')

function handleOpen(payload?: { franquiaId?: number; franchiseName?: string }): void {
  log('SetupFeeMarkPaidModal', 'handleOpen', 'Opening mark-paid modal', payload)
  franquiaId.value = payload?.franquiaId ?? null
  franchiseName.value = payload?.franchiseName ?? ''
  hasFixedFranchise.value = Boolean(payload?.franquiaId)
  reason.value = ''
  errorMessage.value = ''
  isVisible.value = true
}

function handleClose(): void {
  log('SetupFeeMarkPaidModal', 'handleClose', 'Closing mark-paid modal')
  isVisible.value = false
}

async function handleSave(): Promise<void> {
  if (!franquiaId.value) return
  isSaving.value = true
  errorMessage.value = ''

  try {
    const result = await markPaid({
      franquia_id: Number(franquiaId.value),
      ...(reason.value.trim() ? { reason: reason.value.trim() } : {}),
    })
    log('SetupFeeMarkPaidModal', 'handleSave', 'Mark paid succeeded', result)
    eventBus.emit('setup-fee.obligation-updated', result)
    eventBus.emit('subscriptions.refresh')
    eventBus.emit('globalMessage.success', t('Taxa de implantação marcada como paga'))
    handleClose()
  } catch (err) {
    errorMessage.value = (err as Error)?.message || t('Erro ao marcar taxa como paga')
    log('SetupFeeMarkPaidModal', 'handleSave', 'Mark paid error', { error: (err as Error)?.message })
  } finally {
    isSaving.value = false
  }
}

onMounted(() => {
  eventBus.on('modal.setup-fee-mark-paid.open', handleOpen)
})

onBeforeUnmount(() => {
  eventBus.off('modal.setup-fee-mark-paid.open', handleOpen)
})
</script>

<style scoped>
.mark-paid-fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.franchise-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.info-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #4b5563;
}
.info-value {
  font-size: 1rem;
  font-weight: 500;
  color: #111827;
}
.error-message {
  color: #dc3545;
  font-size: 0.875rem;
}
</style>
